import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import ContactAdvertiserButton from "@/components/ContactAdvertiserButton";
import PropertyMap from "../components/PropertyMap";
import { properties } from "@/data/properties";
import { rentalContractInfo } from "@/data/rentalContractInfo";
import { rentalDocuments } from "@/data/rentalDocuments";
import { getPublishedSubmissionById, getPublishedSubmissions } from "@/lib/publishedProperties";
import { getProReportBySubmissionId, mapProReportToAdvisor } from "@/lib/proReports";
import { evaluateAdvisorMatch, logAdvisorDecision } from "@/lib/advisorEngine";
import { hasAdvisorAccess } from "@/lib/advisorAccess";
import { getNearbyPlaces } from "../utils/getNearbyPlaces";
import { formatPrice, getPriceAmount, getRentTaxLabel } from "@/utils/price";
import { getPropertyInegiZoneStats } from "@/utils/inegiZoneStats";
import {
  AlertTriangle,
  Brain,
  Building2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  MapPinned,
  Search,
  Sparkles,
  Store,
  Utensils,
  Users,
  X,
} from "lucide-react";

const getCoordinatesFromProperty = (property) => {
  if (property?.lat && property?.lng) {
    return {
      lat: Number(property.lat),
      lng: Number(property.lng),
    };
  }

  const mapUrl = decodeURIComponent(property?.mapUrl || "");
  const match = mapUrl.match(/q=([-0-9.]+)\s*,\s*([-0-9.]+)/);

  if (!match) return null;

  return {
    lat: Number(match[1]),
    lng: Number(match[2]),
  };
};

const getGroupIcon = (type = "") => {
  const value = type.toLowerCase();

  if (value.includes("comida") || value.includes("sushi")) return Utensils;
  if (value.includes("oficina")) return Building2;
  return Store;
};

const getCompetitionTone = (total) => {
  if (total <= 1) {
    return {
      label: "Competencia baja",
      description:
        total === 0
          ? "No se detectaron negocios similares cerca. Confirma en persona porque los mapas pueden cambiar."
          : "Solo se detecto un negocio similar cerca. Usa esta lectura como referencia y valida la zona.",
      className: "bg-emerald-50 text-emerald-700 border-emerald-100",
      Icon: CheckCircle2,
    };
  }

  if (total <= 3) {
    return {
      label: "Zona con competencia moderada",
      description:
        "Hay algunos negocios similares cerca. Usa esta lectura como referencia rapida y valida la zona.",
      className: "bg-amber-50 text-amber-700 border-amber-100",
      Icon: AlertTriangle,
    };
  }

  return {
    label: "Zona muy competida",
    description:
      "Se detectaron varios competidores directos en 1 km. Confirma cuáles siguen activos y compara su oferta.",
    className: "bg-red-50 text-red-700 border-red-100",
    Icon: AlertTriangle,
  };
};

const getAdvisorScoreTone = (percentage = 0) => {
  if (percentage >= 82) {
    return "border-emerald-100 bg-emerald-50 text-emerald-800";
  }

  if (percentage >= 68) {
    return "border-blue-100 bg-blue-50 text-blue-800";
  }

  if (percentage >= 52) {
    return "border-amber-100 bg-amber-50 text-amber-800";
  }

  return "border-red-100 bg-red-50 text-red-800";
};

const advisorFactorDetails = {
  operacion: {
    title: "Puede operar aquí",
    description:
      "Revisa si el espacio permite la actividad: acceso, maniobra, servicios, restricciones y si el nivel funciona para el giro: planta baja, planta alta o piso específico.",
  },
  ubicacion: {
    title: "Potencial comercial de la ubicación",
    description: "Combina acceso, visibilidad, actividad alrededor y saturación del mismo giro.",
  },
  inmueble: {
    title: "Condiciones del espacio",
    description: "Tamaño, nivel, tipo de inmueble, servicios y características disponibles.",
  },
  competencia: {
    title: "Entorno competitivo",
    description:
      "Distingue si la competencia satura la zona o si forma parte de un corredor reconocido para ese giro.",
  },
  precio: {
    title: "Relación precio-beneficio",
    description: "Compara la renta con los metros cuadrados, la ubicación y las condiciones ofrecidas.",
  },
};

const getAreaSquareMeters = (property = {}) => {
  const details = property?.details || {};
  const candidates = [
    details.metros,
    details.medida,
    details.medidas,
    property.area,
    property.metros,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const normalized = String(candidate).replace(/,/g, ".").replace(/\s+/g, " ");
    const match = normalized.match(/(\d+(?:\.\d+)?)\s*(?:m2|m|metros?)/i);
    if (match) {
      const value = Number(match[1]);
      if (Number.isFinite(value) && value > 0) return value;
    }
  }

  return 0;
};

const getPricePerSquareMeterLabel = (property = {}) => {
  const price = getPriceAmount(property);
  const area = getAreaSquareMeters(property);
  if (!price || !area) return "";

  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price / area);
};

const getAdvisorFactorTone = (value = 0, blocked = false) => {
  const score = Number(value) || 0;
  if (blocked) {
    return { label: "No compatible", bar: "bg-red-500", text: "text-red-700" };
  }
  if (score >= 85) {
    return { label: "Muy favorable", bar: "bg-emerald-500", text: "text-emerald-700" };
  }
  if (score >= 70) {
    return { label: "Favorable", bar: "bg-blue-600", text: "text-blue-700" };
  }
  if (score >= 50) {
    return { label: "Aceptable", bar: "bg-amber-500", text: "text-amber-700" };
  }
  return { label: "Necesita revisión", bar: "bg-orange-500", text: "text-orange-700" };
};

const AdvisorResultList = ({ title, items, tone = "slate" }) => {
  if (!Array.isArray(items) || !items.length) return null;

  const toneClasses = {
    green: "border-emerald-100 bg-emerald-50 text-emerald-900",
    amber: "border-amber-100 bg-amber-50 text-amber-900",
    red: "border-red-100 bg-red-50 text-red-900",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  };

  return (
    <div className={`rounded-xl border p-4 ${toneClasses[tone] || toneClasses.slate}`}>
      <p className="text-xs font-bold uppercase tracking-wide">{title}</p>
      <ul className="mt-3 space-y-2 text-sm leading-5">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PropertyAdvisorCard = ({ businessInput, advisorResult, property }) => {
  const business = businessInput.trim();
  if (!business || !advisorResult) return null;

  const isLowRecommendation = Boolean(
    advisorResult &&
      (advisorResult.percentage < 55 ||
        ["poco_recomendado", "no_recomendado", "no_apto"].includes(advisorResult.status))
  );
  const favorableLocationReasons = (advisorResult?.reasons || []).filter((reason) =>
    /(ubicaci|visib|acceso|esquina|flujo|avenida|estacionamiento)/i.test(reason)
  );
  const pricePerSquareMeter = getPricePerSquareMeterLabel(property);

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-white p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
            <Brain size={22} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-blue-700">
              Resultado del Asesor IA
            </p>
            <h2 className="text-lg font-bold text-slate-950">
              Compatibilidad con esta propiedad
            </h2>
          </div>
        </div>
      </div>

      <div className="p-5">
        {business && advisorResult && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[160px_1fr]">
              <div className={`rounded-2xl border p-5 text-center ${getAdvisorScoreTone(advisorResult.percentage)}`}>
                <p className="text-4xl font-black leading-none">
                  {advisorResult.percentage}%
                </p>
                <p className="mt-2 text-xs font-black uppercase tracking-wide">
                  {advisorResult.tone}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Giro analizado
                </p>
                <p className="mt-1 text-xl font-black text-slate-950">
                  {business}
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Esta lectura combina los datos de la propiedad, el precio, el tipo de
                  espacio, las restricciones, las señales comerciales y el análisis de zona.
                </p>
              </div>
            </div>

            {isLowRecommendation ? (
              <>
                <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-950">
                  <p className="font-black">
                    No recomendamos avanzar con esta propiedad para {business}.
                  </p>
                  <p className="mt-1 text-sm leading-6">
                    Aunque el espacio tenga puntos a favor, los datos de zona, competencia o condiciones reducen su compatibilidad para este giro. Conviene comparar otras opciones antes de avanzar.
                  </p>
                </div>
                <AdvisorResultList
                  title="Por qué no la recomendamos"
                  items={advisorResult.risks}
                  tone="red"
                />
                {favorableLocationReasons.length > 0 && (
                  <AdvisorResultList
                    title="Dato favorable que no cambia la recomendación"
                    items={favorableLocationReasons.slice(0, 1)}
                    tone="slate"
                  />
                )}
              </>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <AdvisorResultList
                  title="Por qué puede funcionar"
                  items={advisorResult.reasons}
                  tone="green"
                />
                <AdvisorResultList
                  title="Revisa antes de decidir"
                  items={advisorResult.risks}
                  tone="amber"
                />
              </div>
            )}

            {advisorResult.ahp?.scores && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-sm font-black text-slate-950">
                  Qué tomamos en cuenta
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Combinamos estos puntos para saber qué tan compatible es la propiedad con el giro.
                </p>
                <div className="mt-4 divide-y divide-slate-100">
                  {Object.entries(advisorResult.ahp.scores).map(([key, value]) => {
                    const detail = advisorFactorDetails[key] || {
                      title: key,
                      description: "Información considerada para calcular la compatibilidad.",
                    };
                    const operationBlocked =
                      key === "operacion" && advisorResult.status === "no_apto";
                    const tone = getAdvisorFactorTone(value, operationBlocked);

                    return (
                      <div key={key} className="grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(180px,240px)] sm:items-center">
                        <div>
                          <p className="text-sm font-black text-slate-900">{detail.title}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">{detail.description}</p>
                          {key === "precio" && pricePerSquareMeter && (
                            <p className="mt-1 text-xs leading-5 text-slate-600">
                              Renta aproximada por m²:{" "}
                              <span className="font-black text-slate-900">{pricePerSquareMeter}</span>.
                            </p>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center justify-between gap-3 text-xs font-bold">
                            <span className={tone.text}>{tone.label}</span>
                            <span className="text-slate-900">{Math.round(value)}%</span>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full ${tone.bar}`}
                              style={{ width: `${Math.max(3, Math.min(100, Number(value) || 0))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

const DemoAudienceCards = ({ sidebar = false }) => (
  <div
    className={`${
      sidebar
        ? "grid gap-5 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1"
        : "mt-6 grid gap-4 md:grid-cols-2"
    }`}
  >
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-[#F97316] ring-1 ring-orange-100">
          <Building2 className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-black uppercase tracking-wide text-[#F97316]">
            Arrendador
          </p>
          <h3 className="mt-1 text-lg font-black text-slate-950">
            Así se puede ver tu propiedad en Plan Pro
          </h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
            La IA te ayuda a mostrar mejor el valor de la propiedad, atraer
            arrendatarios más confiables y aumentar tus posibilidades de rentar
            más rápido y por más tiempo. Presenta tu espacio con datos
            relevantes, lectura de zona y señales comerciales que ayudan a
            posicionarlo de forma inteligente.
          </p>
        </div>
      </div>
    </div>

    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#0056D2] ring-1 ring-blue-100">
          <Users className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-black uppercase tracking-wide text-[#0056D2]">
            Arrendatario
          </p>
          <h3 className="mt-1 text-lg font-black text-slate-950">
            Evalúa una ubicación en segundos con IA
          </h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
            Esta información te ayuda a tomar decisiones con más claridad y a
            estimar si tu giro tiene buenas posibilidades de funcionar en esa
            ubicación. Usa datos de zona, competencia, flujo y características
            del espacio para comparar mejor cada propiedad.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const normalizeZoneText = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const zoneBusinessProfiles = [
  {
    key: "barberia",
    aliases: [
      "barberia",
      "barber",
      "barbershop",
      "estetica",
      "salon de belleza",
      "peluqueria",
      "corte de pelo",
      "corte de cabello",
      "tinte",
      "alisado",
      "barba",
    ],
    label: "barberia, estetica o salon de belleza",
    highCompetition:
      "La zona ya tiene oferta de barberias, esteticas o salones de belleza. Puede funcionar si el concepto se distingue por cortes, color, barba, agenda rapida, imagen y clientes recurrentes.",
    moderateCompetition:
      "Hay competencia manejable para cortes de cabello, barberia o salon. Conviene diferenciar servicio, horarios, reseñas y promociones para clientes recurrentes.",
    lowCompetition:
      "No aparece mucha competencia directa de barberia, estetica o salon. Puede ser buena senal si alrededor hay vivienda, oficinas o negocios que generen visitas frecuentes.",
    demandDrivers: ["vivienda cercana", "oficinas", "gimnasios", "boutiques", "servicios de salud"],
    contextKeywords: ["belleza", "gimnasio", "oficina", "clinica", "consultorio", "tienda", "ropa", "boutique"],
    validation: "Valida flujo de tarde y sabados, porque este giro depende mucho de repeticion y recomendacion.",
  },
  {
    key: "spa",
    aliases: [
      "spa",
      "masaje",
      "masajes",
      "facial",
      "faciales",
      "terapia",
      "cosmetologia",
      "cosmetologa",
      "skin care",
      "skincare",
      "wellness",
      "centro de bienestar",
      "bienestar",
      "tratamientos corporales",
      "depilacion",
      "manicure",
      "pedicure",
      "aromaterapia",
      "rejuvenecimiento",
    ],
    label: "spa, cosmetologia o bienestar",
    highCompetition:
      "La zona ya tiene oferta de spa, cosmetologia o bienestar. Puede funcionar si el concepto se distingue por experiencia, confianza, especialidad, agenda y tratamientos claros.",
    moderateCompetition:
      "Hay competencia manejable para spa o tratamientos corporales. Conviene diferenciar faciales, masajes, skin care, depilacion, manicure/pedicure o paquetes de bienestar.",
    lowCompetition:
      "No aparece mucha competencia directa de spa o bienestar. Puede ser oportunidad si hay vivienda, oficinas y consumo recurrente cerca.",
    demandDrivers: ["vivienda cercana", "oficinas", "gimnasios", "salud", "estacionamiento"],
    contextKeywords: ["gimnasio", "salud", "clinica", "consultorio", "oficina", "belleza"],
    validation: "Valida privacidad, ambiente interior, estacionamiento y horarios fuertes de tarde o fin de semana.",
  },
  {
    key: "artes_marciales",
    aliases: ["karate", "taekwondo", "artes marciales", "dojo", "box", "boxeo", "mma"],
    label: "artes marciales y entrenamiento",
    highCompetition:
      "Hay varios centros deportivos o academias cerca. Entrar puede funcionar si el horario, la disciplina o el enfoque familiar son diferentes.",
    moderateCompetition:
      "La competencia deportiva es moderada. La zona puede responder si hay familias, escuelas o colonias alrededor.",
    lowCompetition:
      "Se ve poca competencia directa de entrenamiento. Puede ser oportunidad si el local tiene visibilidad, ventilacion y espacio suficiente.",
    demandDrivers: ["escuelas", "colonias habitacionales", "gimnasios", "parques", "avenidas con acceso facil"],
    contextKeywords: ["escuela", "colegio", "gimnasio", "deporte", "parque", "oficina", "tienda"],
    validation: "Revisa estacionamiento y seguridad de acceso en horarios de tarde-noche.",
  },
  {
    key: "sushi",
    aliases: ["sushi", "japonesa", "roll", "rollos"],
    label: "sushi y comida japonesa",
    highCompetition:
      "Hay alta competencia de sushi. Para destacar, define una diferencia clara en producto, precio, entrega o experiencia.",
    moderateCompetition:
      "Hay competencia moderada de sushi. Conviene competir con calidad, rapidez, combos y presencia fuerte en redes.",
    lowCompetition:
      "No aparece mucha competencia directa de sushi. Si hay consumo de comida cerca, puede ser una oportunidad para entrar con marca clara.",
    demandDrivers: ["oficinas", "colonias habitacionales", "escuelas", "gimnasios", "comercios de consumo diario"],
    contextKeywords: ["comida", "restaurante", "tienda", "abarrotes", "oficina", "escuela", "gimnasio", "cafe"],
    validation: "Valida demanda en comida y cena, ademas de acceso para repartidores.",
  },
  {
    key: "cafeteria",
    aliases: ["cafeteria", "cafe", "coffee", "restaurante", "comida", "taqueria", "hamburguesa", "pizzeria", "panaderia"],
    label: "comida, cafeteria o restaurante",
    highCompetition:
      "La zona tiene mucha oferta de comida. Puede funcionar si el concepto resuelve algo especifico: desayunos, comida rapida, antojo, delivery o experiencia.",
    moderateCompetition:
      "Hay competencia moderada de comida. El giro puede funcionar si se diferencia por horario, ticket promedio, rapidez o producto estrella.",
    lowCompetition:
      "Aparece poca competencia directa de comida. Puede ser oportunidad si hay oficinas, escuelas o vivienda que sostengan consumo diario.",
    demandDrivers: ["oficinas", "escuelas", "hospitales", "paradas de transporte", "comercios con empleados"],
    contextKeywords: ["oficina", "escuela", "clinica", "hospital", "tienda", "abarrotes", "banco", "gimnasio", "farmacia"],
    validation: "Observa flujo en desayuno, comida y tarde; el horario fuerte cambia mucho segun la zona.",
  },
  {
    key: "farmacia",
    aliases: ["farmacia", "pharmacy", "salud", "clinica", "consultorio", "doctor", "medico"],
    label: "farmacia o salud",
    highCompetition:
      "La zona ya tiene oferta de salud o farmacias. Entrar puede funcionar si hay diferenciador: horario extendido, consultorio, productos especializados o servicio rapido.",
    moderateCompetition:
      "Hay competencia moderada de salud. La cercania con consultorios, clinicas o vivienda puede sostener demanda recurrente.",
    lowCompetition:
      "No aparece mucha competencia directa de farmacia o salud. Puede ser atractivo si hay colonias, consultorios o flujo peatonal cerca.",
    demandDrivers: ["clinicas", "consultorios", "colonias habitacionales", "adultos mayores", "avenidas faciles de ubicar"],
    contextKeywords: ["clinica", "consultorio", "doctor", "salud", "hospital", "farmacia", "tienda", "abarrotes"],
    validation: "Confirma permisos, visibilidad y facilidad para compras rapidas.",
  },
  {
    key: "ferreteria",
    aliases: ["ferreteria", "herramientas", "materiales", "construccion", "pintura", "electricos", "plomeria"],
    label: "ferreteria, herramientas o materiales",
    highCompetition:
      "Zona competida para ferreteria y materiales. Puede funcionar si manejas inventario especializado, entregas rapidas, precio claro o atencion tecnica.",
    moderateCompetition:
      "Hay competencia moderada de ferreteria. Conviene diferenciar por surtido, horario, servicio a domicilio o productos que no tengan los cercanos.",
    lowCompetition:
      "No aparece mucha competencia directa de ferreteria. Puede ser oportunidad si hay vivienda, talleres o construccion alrededor.",
    demandDrivers: ["colonias habitacionales", "talleres", "constructoras", "vialidades con acceso vehicular", "estacionamiento"],
    contextKeywords: ["taller", "materiales", "construccion", "pintura", "electric", "plomer", "tienda", "abarrotes", "servicio"],
    validation: "Revisa carga y descarga, estacionamiento y facilidad para clientes que llegan en carro.",
  },
  {
    key: "consultorio",
    aliases: ["consultorio", "clinica", "doctor", "medico", "odontologo", "dentista", "nutriologo"],
    label: "consultorio o clinica",
    highCompetition:
      "Hay varios servicios medicos cerca. Eso puede indicar demanda, pero obliga a especializarse y generar confianza desde imagen, agenda y ubicacion.",
    moderateCompetition:
      "La competencia medica es moderada. Puede funcionar bien si el local se percibe profesional y es facil de encontrar.",
    lowCompetition:
      "Se detecta poca competencia directa de consultorios. Puede ser oportunidad si hay vivienda, oficinas o farmacias alrededor.",
    demandDrivers: ["farmacias", "laboratorios", "oficinas", "vivienda", "estacionamiento"],
    contextKeywords: ["farmacia", "clinica", "hospital", "laboratorio", "oficina", "salud", "tienda"],
    validation: "Valida privacidad, estacionamiento y facilidad de acceso para pacientes.",
  },
  {
    key: "oficina",
    aliases: ["oficina", "despacho", "corporativo", "coworking"],
    label: "oficina o despacho",
    highCompetition:
      "Hay presencia de oficinas o despachos. Puede funcionar si el local ofrece mejor ubicacion, imagen, estacionamiento o distribucion interior.",
    moderateCompetition:
      "Hay actividad de oficinas manejable. La zona puede ser util para servicios profesionales si tiene acceso claro y buena percepcion.",
    lowCompetition:
      "Se detectan pocas oficinas cercanas. Puede servir para despacho si la zona tiene clientes potenciales o esta bien conectada.",
    demandDrivers: ["bancos", "notarias", "consultorios", "cafeterias", "avenidas principales"],
    contextKeywords: ["oficina", "banco", "consultorio", "notaria", "cafe", "restaurante", "servicio"],
    validation: "Revisa ruido, estacionamiento y facilidad para recibir clientes.",
  },
  {
    key: "gimnasio",
    aliases: ["gimnasio", "gym", "fitness", "crossfit"],
    label: "gimnasio o fitness",
    highCompetition:
      "La zona tiene competencia fitness. Puede funcionar si el concepto es distinto: entrenamiento personalizado, horario extendido, clases o nicho claro.",
    moderateCompetition:
      "Hay competencia moderada de gimnasios. Conviene diferenciar comunidad, precio, entrenadores y experiencia.",
    lowCompetition:
      "No aparece mucha competencia fitness. Puede ser oportunidad si hay vivienda, oficinas o escuelas cerca.",
    demandDrivers: ["colonias habitacionales", "oficinas", "escuelas", "esteticas", "comida saludable"],
    contextKeywords: ["vivienda", "oficina", "escuela", "belleza", "salud", "comida", "tienda"],
    validation: "Confirma espacio, ventilacion, ruido permitido y estacionamiento en horas pico.",
  },
  {
    key: "tienda",
    aliases: ["tienda", "abarrotes", "market", "super", "minisuper", "mini super"],
    label: "tienda, abarrotes o minisuper",
    highCompetition:
      "Hay varias tiendas cercanas. Puede funcionar si el local gana por cercania, surtido, horario, precio o servicio a domicilio.",
    moderateCompetition:
      "La competencia de tiendas es moderada. Hay oportunidad si se atiende mejor una necesidad diaria de la zona.",
    lowCompetition:
      "Aparece poca competencia directa de tiendas. Puede ser buena senal si hay casas, escuelas u oficinas alrededor.",
    demandDrivers: ["vivienda", "escuelas", "oficinas", "paradas de transporte", "comercios con empleados"],
    contextKeywords: ["tienda", "abarrotes", "escuela", "oficina", "comida", "farmacia", "transporte"],
    validation: "Revisa flujo peatonal, compras de paso y competencia informal cercana.",
  },
  {
    key: "papeleria",
    aliases: ["papeleria", "copias", "copy", "imprenta", "impresion"],
    label: "papeleria, copias o imprenta",
    highCompetition:
      "Hay competencia de papeleria o copias. Puede funcionar si agregas impresiones rapidas, tramites, diseno o servicios escolares.",
    moderateCompetition:
      "La competencia es manejable. Conviene apoyarse en escuelas, oficinas y servicios de impresion de urgencia.",
    lowCompetition:
      "Se detecta poca competencia directa. Puede ser oportunidad si hay escuelas, oficinas o colonias cerca.",
    demandDrivers: ["escuelas", "oficinas", "dependencias", "colonias habitacionales", "consultorios"],
    contextKeywords: ["escuela", "oficina", "consultorio", "tienda", "servicio", "banco"],
    validation: "Valida horarios escolares/oficina y visibilidad para compras rapidas.",
  },
  {
    key: "yonke",
    aliases: [
      "yonke",
      "yonkes",
      "deshuesadero",
      "autopartes usadas",
      "auto partes usadas",
      "refacciones usadas",
      "auto salvage",
      "junkyard",
    ],
    label: "yonke, deshuesadero o autopartes usadas",
    highCompetition:
      "La zona ya tiene varios yonkes o negocios de autopartes usadas. Eso puede indicar demanda del giro, pero conviene competir con inventario claro, rapidez de respuesta, confianza y buena visibilidad.",
    moderateCompetition:
      "Hay competencia manejable de yonkes o autopartes usadas. Puede funcionar si el negocio se diferencia por surtido usado, ubicacion, facilidad de acceso y atencion rapida.",
    lowCompetition:
      "Se detecta poca competencia directa de yonkes o autopartes usadas. Puede ser oportunidad si la zona tiene flujo vehicular, talleres y acceso facil para clientes.",
    demandDrivers: ["talleres mecanicos", "flujo vehicular", "acceso para carga", "vialidades principales"],
    contextKeywords: ["taller", "gasolinera", "servicios para autos", "ferreteria"],
    validation: "Valida permisos, accesos, maniobras y visibilidad desde la vialidad principal.",
  },
  {
    key: "llantera",
    aliases: ["llantera", "llanteria", "llantas", "venta de llantas", "tire shop"],
    label: "llantera",
    highCompetition:
      "La zona ya tiene varias llanteras. Puede funcionar si se diferencia por inventario, rapidez, instalacion, alineacion o servicio a flotillas.",
    moderateCompetition:
      "Hay competencia manejable de llanteras. Conviene revisar precios, horarios, espacio para maniobras y facilidad de acceso.",
    lowCompetition:
      "Se detecta poca competencia directa de llanteras. Puede ser oportunidad si hay flujo vehicular y servicios automotrices cercanos.",
    demandDrivers: ["flujo vehicular", "talleres mecanicos", "gasolineras", "acceso para autos"],
    contextKeywords: ["taller", "gasolinera", "servicios para autos", "refaccionaria"],
    validation: "Valida espacio de maniobra, visibilidad y acceso seguro para vehiculos.",
  },
  {
    key: "refaccionaria",
    aliases: [
      "refaccionaria",
      "refacciones",
      "autopartes",
      "auto partes",
      "partes para auto",
      "auto parts store",
    ],
    label: "refaccionaria o autopartes nuevas",
    highCompetition:
      "La zona ya tiene varias refaccionarias. Puede funcionar si se diferencia por surtido, marcas, disponibilidad inmediata, entregas y atencion a talleres.",
    moderateCompetition:
      "Hay competencia manejable de refaccionarias. Conviene revisar que tipos de autos, camiones o trailers atienden los negocios cercanos.",
    lowCompetition:
      "Se detecta poca competencia directa de refaccionarias. Puede ser oportunidad si hay talleres, transporte de carga o flujo vehicular alrededor.",
    demandDrivers: ["talleres mecanicos", "transportistas", "flujo vehicular", "entrega rapida"],
    contextKeywords: ["taller", "gasolinera", "servicios para autos", "llantera"],
    validation: "Valida surtido requerido por la zona y si los talleres cercanos compran piezas nuevas o usadas.",
  },
];

const defaultZoneProfile = {
  key: "negocio",
  label: "este giro",
  highCompetition:
    "La zona tiene competencia directa. Puede funcionar si la propuesta es clara y diferente para el cliente.",
  moderateCompetition:
    "Hay competencia moderada. Conviene diferenciar servicio, precio, horario o especialidad.",
  lowCompetition:
    "Se detecta poca competencia directa. Puede ser oportunidad si el entorno realmente tiene flujo y clientes compatibles.",
  demandDrivers: ["flujo real", "visibilidad", "facil acceso", "negocios complementarios"],
  contextKeywords: ["tienda", "oficina", "comida", "servicio", "salud", "escuela"],
  validation: "Valida la zona en los horarios fuertes de tu giro antes de decidir.",
};

const demoPropertyId = "26";
const demoBusinessInput = "Farmacia";
const demoAgentPath = `/propiedad/${demoPropertyId}?demo=agentes`;
const demoSuggestionMessage =
  "Esta propiedad aun no tiene activos los agentes de inteligencia artificial, pero puedes ver como funcionan con nuestro demo gratis.";
const aiDisclaimerText =
  "Usamos inteligencia artificial para ordenar la informacion. Puede haber errores o datos incompletos, como en cualquier herramienta de IA.";

const getZoneAnalysisStorageKey = (propertyId) =>
  propertyId ? `tulocalmx:zone-analysis:v4:${propertyId}` : "";

const readSavedZoneAnalysis = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return null;

  try {
    return JSON.parse(window.localStorage.getItem(storageKey) || "null");
  } catch (error) {
    return null;
  }
};

const saveZoneAnalysis = (storageKey, payload) => {
  if (!storageKey || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        updatedAt: new Date().toISOString(),
        savedLocally: false,
        message: "Analisis demasiado grande para guardar localmente",
      })
    );
  } catch (error) {
    console.warn("No se pudo guardar el analisis local:", error);
  }
};

const getZoneBusinessProfile = (nearby) => {
  const source = normalizeZoneText(
    `${nearby?.businessType || ""} ${nearby?.competitionLabel || ""}`
  );

  return (
    zoneBusinessProfiles.find((profile) =>
      profile.aliases.some((alias) => source.includes(normalizeZoneText(alias)))
    ) || defaultZoneProfile
  );
};

const getClosestDistanceText = (nearby) => {
  const distances = (nearby?.directCompetition?.items || [])
    .map((item) => Number(item.distanceMeters))
    .filter((distance) => Number.isFinite(distance))
    .sort((a, b) => a - b);

  if (!distances.length) return "";

  if (distances[0] <= 250) {
    return " El competidor mas cercano aparece muy cerca, asi que la diferenciacion debe ser evidente.";
  }

  if (distances[0] <= 600) {
    return " Hay competencia a distancia caminable, por lo que conviene revisar precios y concepto.";
  }

  return " La competencia detectada no parece estar pegada al local, lo cual da margen para posicionarse.";
};

const getMatchingContextGroups = (groups, profile) => {
  const keywords = profile.contextKeywords.map(normalizeZoneText);

  return groups
    .map((group) => group.type)
    .filter((type) => {
      const normalizedType = normalizeZoneText(type);
      return keywords.some((keyword) => normalizedType.includes(keyword));
    })
    .slice(0, 3);
};

const buildZoneSummary = (nearby, tone) => {
  const directTotal =
    nearby.directCompetition.nearbyTotal ?? nearby.directCompetition.total;
  const otherTotal = nearby.otherBusinesses.total;
  const groups = nearby.otherBusinesses.groups || [];
  const profile = getZoneBusinessProfile(nearby);
  const topGroups = groups.slice(0, 3).map((group) => group.type.toLowerCase());
  const matchingGroups = getMatchingContextGroups(groups, profile);
  const closestDistanceText = getClosestDistanceText(nearby);

  const competitionText =
    directTotal > 3
      ? `${profile.highCompetition}${closestDistanceText}`
      : directTotal >= 2
        ? `${profile.moderateCompetition}${closestDistanceText}`
        : profile.lowCompetition;

  const commercialText =
    otherTotal >= 15
      ? `Se detectaron ${otherTotal} negocios y lugares cercanos, señal de actividad comercial alta. Para ${profile.label}, conviene confirmar flujo de ${profile.demandDrivers.slice(0, 3).join(", ")}.`
      : otherTotal >= 6
        ? `Se detectaron ${otherTotal} negocios y lugares cercanos, señal de actividad comercial media. Confirma flujo de ${profile.demandDrivers.slice(0, 2).join(" y ")}.`
        : `Solo se detectaron ${otherTotal} negocios y lugares cercanos. Confirma en sitio el flujo de clientes, la visibilidad y la actividad por horario.`;

  const contextText =
    matchingGroups.length > 0
      ? `Categorías cercanas que pueden aportar clientes: ${matchingGroups.join(", ")}. ${profile.validation}`
      : topGroups.length > 0
        ? `Categorías más comunes cerca: ${topGroups.join(", ")}. Confirma si sus clientes también consumen ${profile.label}. ${profile.validation}`
        : `No hay suficientes categorías cercanas para identificar negocios complementarios. ${profile.validation}`;

  return {
    title: tone.label,
    competitionText,
    commercialText,
    contextText,
  };
};

const BusinessGroupList = ({ groups, emptyText, featured = false }) => {
  if (!groups || groups.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {groups.slice(0, 8).map((group) => {
        const Icon = getGroupIcon(group.type);
        const cleanBusinessName = (name = "") =>
          name
            .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "")
            .replace(/\s+/g, " ")
            .trim();
        const getMapsUrl = (item) => {
          if (Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng))) {
            return `https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lng}`;
          }

          return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            item.name
          )}`;
        };
        const namedItems = group.items
          .filter((item) => item.name)
          .map((item) => ({
            ...item,
            name: cleanBusinessName(item.name),
          }))
          .filter((item) => item.name)
          .filter(
            (item, index, list) =>
              list.findIndex((entry) => entry.name === item.name) === index
          )
          .slice(0, 10);

        return (
          <div
            key={group.type}
            className={`rounded-xl border border-gray-100 bg-white p-4 shadow-sm ${
              featured ? "md:col-span-2" : ""
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <Icon size={20} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-gray-900">{group.type}</p>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700">
                    {group.total}
                  </span>
                </div>

                {namedItems.length > 0 && (
                  <ol
                    className={`mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-xs leading-5 text-gray-500 ${
                      featured ? "sm:grid-cols-2 lg:grid-cols-2" : "sm:grid-cols-2"
                    }`}
                  >
                    {namedItems.map((item, index) => (
                      <li key={item.name} className="flex gap-2">
                        <span className="shrink-0 font-semibold text-gray-400">
                          {index + 1}.
                        </span>
                        <span>
                          <a
                            href={getMapsUrl(item)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-700 underline-offset-2 hover:underline"
                          >
                            {item.name}
                          </a>
                          {Number.isFinite(Number(item.distanceMeters)) && (
                            <span className="ml-1 text-gray-400">
                              ({Number(item.distanceMeters)} m)
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                    {group.items.length > namedItems.length && (
                      <li className="pl-5 text-gray-400 sm:col-span-2">
                        Y otros negocios cercanos.
                      </li>
                    )}
                  </ol>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString("es-MX");

const PopulationSummary = ({ population }) => {
  if (!population?.radii?.length) return null;

  const items = population.radii
    .filter((item) => item.radiusMeters === 1000 || item.radiusMeters === 5000)
    .sort((a, b) => a.radiusMeters - b.radiusMeters);

  if (!items.length) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
          <Users size={22} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            Habitantes alrededor
          </h3>
          <p className="text-sm text-gray-500">
            Conteo con manzanas de INEGI dentro del radio del local.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((item) => (
          <div
            key={item.radiusMeters}
            className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
              Radio {item.radiusMeters / 1000} km
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {formatCount(item.population)}
            </p>
            <p className="text-xs text-gray-500">
              habitantes en {formatCount(item.blocks)} manzanas
            </p>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs leading-5 text-gray-500">
        Fuente: {population.source}. Metodo: {population.method}
      </p>
    </div>
  );
};

const InegiZoneSummary = ({ stats }) => {
  if (!stats) return null;

  const items = [
    { label: "Habitantes", value: stats.population, icon: Users },
    { label: "Mujeres", value: stats.women, icon: Users },
    { label: "Hombres", value: stats.men, icon: Users },
    { label: "Viviendas habitadas", value: stats.occupiedHomes, icon: Building2 },
  ];

  return (
    <div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
          <Users size={22} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            Datos INEGI alrededor del espacio
          </h3>
          <p className="text-sm text-gray-500">
            Radio de {stats.radiusMeters / 1000} km con manzanas cercanas al local.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="rounded-xl border border-blue-100 bg-blue-50/60 p-4"
            >
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-700">
                <Icon size={14} />
                {item.label}
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {formatCount(item.value)}
              </p>
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-xs leading-5 text-gray-500">
        Fuente: INEGI SCINCE 2020. Viviendas habitadas significa viviendas particulares habitadas.
      </p>
    </div>
  );
};

const formatGoogleConsultedAt = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const ZoneAnalysis = ({ nearby, inegiStats }) => {
  if (!nearby) return null;

  const directNearbyTotal =
    nearby.directCompetition.nearbyTotal ?? nearby.directCompetition.total;
  const directExpandedTotal =
    nearby.directCompetition.expandedTotal ?? nearby.directCompetition.total;
  const otherTotal = nearby.otherBusinesses.total;
  const totalDetected = directNearbyTotal + otherTotal;
  const tone = getCompetitionTone(directNearbyTotal);
  const ToneIcon = tone.Icon;
  const zoneSummary = buildZoneSummary(nearby, tone);
  const confidence = nearby.confidence;
  const consultedAt = formatGoogleConsultedAt(nearby.googleConsultedAt);
  const confidenceTone =
    confidence?.level === "alto"
      ? "border-emerald-100 bg-emerald-50 text-emerald-800"
      : confidence?.level === "medio"
        ? "border-amber-100 bg-amber-50 text-amber-800"
        : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <div className="mt-6 space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-white p-5 text-slate-950">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Entorno {nearby.radiusMeters / 1000} km
              </p>
              <h2 className="mt-1 text-xl font-bold">Competencia en la zona</h2>
              <p className="mt-1 text-sm text-slate-600">
                El diagnostico principal toma competencia directa y comercios cercanos dentro de 1 km.
              </p>
              {nearby.demo && (
                <p className="mt-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                  Demo de agentes
                </p>
              )}
            </div>

            <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${tone.className}`}>
              <ToneIcon size={18} />
              {tone.label}
            </div>
          </div>

          {(confidence || consultedAt) && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {confidence && (
                <div className={`rounded-xl border px-4 py-3 ${confidenceTone}`}>
                  <p className="text-xs font-bold uppercase tracking-wide">
                    Confianza del análisis
                  </p>
                  <p className="mt-1 text-sm font-semibold capitalize">
                    {confidence.level || "medio"}
                    {confidence.score ? `, puntaje ${confidence.score}` : ""}
                  </p>
                  <p className="mt-1 text-xs leading-5">
                    {confidence.summary}
                  </p>
                </div>
              )}

              {consultedAt && (
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-blue-800">
                  <p className="text-xs font-bold uppercase tracking-wide">
                    Información de Google
                  </p>
                  <p className="mt-1 text-sm font-semibold">
                    {nearby.cache?.hit ? "Guardado y vigente" : "Consultado"} el {consultedAt}
                  </p>
                  <p className="mt-1 text-xs leading-5">
                    Se usan señales como negocios activos, reseñas, horarios, teléfono y ficha de Google.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 divide-x divide-gray-100">
          <div className="p-4">
            <p className="text-2xl font-bold text-gray-900">{directNearbyTotal}</p>
            <p className="text-xs text-gray-500">Competidores 1 km</p>
          </div>
          <div className="p-4">
            <p className="text-2xl font-bold text-gray-900">{totalDetected}</p>
            <p className="text-xs text-gray-500">Negocios y lugares cercanos (1 km)</p>
          </div>
        </div>
      </div>

      <InegiZoneSummary stats={inegiStats} />
      {!inegiStats && <PopulationSummary population={nearby.population} />}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-5 lg:grid-cols-[1fr_1.1fr]">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
            <MapPinned size={22} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {nearby.recommendation?.viability || "Zona con potencial"}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {tone.description}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-4">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">
            Lectura práctica de la zona
          </h3>
          <div className="mt-3 space-y-3 text-sm leading-6 text-gray-600">
            <div>
              <p className="font-semibold text-gray-900">Competencia directa</p>
              <p>{zoneSummary.competitionText}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900">Actividad alrededor</p>
              <p>{zoneSummary.commercialText}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900">Qué conviene validar</p>
              <p>{zoneSummary.contextText}</p>
            </div>
          </div>
        </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg text-gray-900">
              Competencia directa
            </h3>
            <p className="text-sm text-gray-500">
              Competidores directos detectados por nombre, categoria y distancia.
            </p>
          </div>
        </div>

        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-orange-700">
              Cerca
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {directNearbyTotal}
            </p>
            <p className="text-xs text-gray-500">competidores dentro de 1 km</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Alcance ampliado
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {directExpandedTotal}
            </p>
            <p className="text-xs text-gray-500">competidores hasta 5 km</p>
          </div>
        </div>

        <BusinessGroupList
          groups={nearby.directCompetition.groups}
          emptyText="No aparece competencia directa registrada hasta 5 km."
          featured
        />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg text-gray-900">
              Entorno comercial agrupado
            </h3>
            <p className="text-sm text-gray-500">
              Los negocios cercanos se ordenan por tipo para ver rapido que hay alrededor.
            </p>
          </div>

          <span className="rounded-full bg-green-50 px-3 py-1 text-sm font-bold text-green-700">
            {nearby.otherBusinesses.total}
          </span>
        </div>

        <BusinessGroupList
          groups={nearby.otherBusinesses.groups}
          emptyText="No aparecen otros negocios registrados dentro de 1 km."
        />
      </div>
    </div>
  );
};

const AsesorList = ({ title, items, tone = "slate" }) => {
  if (!Array.isArray(items) || !items.length) return null;

  const toneClassNames = {
    green: "border-emerald-100 bg-emerald-50/70 text-emerald-800",
    orange: "border-orange-100 bg-orange-50/70 text-orange-800",
    red: "border-red-100 bg-red-50/70 text-red-800",
    blue: "border-blue-100 bg-blue-50/70 text-blue-800",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  };

  return (
    <div className={`rounded-xl border p-4 ${toneClassNames[tone] || toneClassNames.slate}`}>
      <p className="text-xs font-bold uppercase tracking-wide">{title}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.map((item) => (
          <span
            key={item}
            className="rounded-full bg-white/80 px-3 py-1 text-xs font-semibold shadow-sm ring-1 ring-black/5"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

const AsesorTextList = ({ title, items }) => {
  if (!Array.isArray(items) || !items.length) return null;

  return (
    <div>
      <p className="text-sm font-bold text-slate-950">{title}</p>
      <ul className="mt-2 space-y-2 text-sm leading-5 text-slate-700">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AsesorIASection = ({ asesor }) => {
  if (!asesor) return null;

  const hasProfile =
    asesor.perfilCliente &&
    (asesor.perfilCliente.consumidorPrincipal?.length ||
      asesor.perfilCliente.ticketZona ||
      asesor.perfilCliente.nivelSocioeconomicoEstimado ||
      asesor.perfilCliente.notas?.length);

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-950 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-orange-300 ring-1 ring-white/15">
            <Brain size={22} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-orange-200">
              Asesor IA
            </p>
            <h2 className="text-lg font-bold">
              Recomendaciones comerciales
            </h2>
          </div>
        </div>
      </div>

      <div className="space-y-5 p-5">
        <div className="grid gap-3 md:grid-cols-2">
          <AsesorList title="Usos sugeridos" items={asesor.usosSugeridos} tone="green" />
          <AsesorList title="Generadores de demanda" items={asesor.generadoresDemanda} tone="blue" />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <AsesorList title="Giros en la misma plaza" items={asesor.girosMismaPlaza} />
          <AsesorList title="Competencia cercana" items={asesor.competenciaCercana} />
          <AsesorList title="Competencia misma plaza" items={asesor.competenciaMismaPlaza} />
        </div>

        {hasProfile && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Perfil del cliente
            </p>
            <div className="mt-3 grid gap-4 md:grid-cols-[1fr_1.2fr]">
              <div className="space-y-2 text-sm text-slate-700">
                {asesor.perfilCliente.ticketZona && (
                  <p>
                    <b>Ticket:</b> {asesor.perfilCliente.ticketZona}
                  </p>
                )}
                {asesor.perfilCliente.nivelSocioeconomicoEstimado && (
                  <p>
                    <b>Nivel estimado:</b> {asesor.perfilCliente.nivelSocioeconomicoEstimado}
                  </p>
                )}
                {asesor.perfilCliente.confianza && (
                  <p>
                    <b>Confianza:</b> {asesor.perfilCliente.confianza}
                  </p>
                )}
              </div>
              <AsesorList
                title="Consumidor principal"
                items={asesor.perfilCliente.consumidorPrincipal}
                tone="blue"
              />
            </div>
            <div className="mt-4">
              <AsesorTextList title="Notas" items={asesor.perfilCliente.notas} />
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <AsesorTextList title="Ventajas" items={asesor.ventajas} />
          <AsesorTextList title="Riesgos" items={asesor.riesgos} />
          <AsesorTextList title="Restricciones operativas" items={asesor.restriccionesOperativas} />
        </div>

        {asesor.validacionCampo && (
          <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-4 text-sm leading-6 text-amber-950">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700">
              Validacion de campo
            </p>
            <p className="mt-2">
              <b>Fecha:</b> {asesor.validacionCampo.fecha || "Sin fecha"}
            </p>
            {asesor.validacionCampo.flujoObservado && (
              <p>
                <b>Flujo observado:</b> {asesor.validacionCampo.flujoObservado}
              </p>
            )}
            {asesor.validacionCampo.horarioFuerte?.length > 0 && (
              <p>
                <b>Horario fuerte:</b> {asesor.validacionCampo.horarioFuerte.join(", ")}
              </p>
            )}
            {asesor.validacionCampo.estacionamientoObservado && (
              <p>
                <b>Estacionamiento:</b> {asesor.validacionCampo.estacionamientoObservado}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ProReportSummary = ({ report }) => {
  if (!report) return null;

  const generatedAt = report.generated_at
    ? new Date(report.generated_at).toLocaleDateString("es-MX")
    : "";

  return (
    <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
      <div className="border-b border-blue-50 bg-blue-50/60 px-5 py-4">
        <p className="text-[10px] font-bold uppercase tracking-wide text-blue-700">
          Plan Pro
        </p>
        <h2 className="mt-1 text-lg font-black text-slate-950">
          Reporte de zona guardado
        </h2>
      </div>
      <div className="space-y-4 p-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase text-slate-500">Score</p>
            <p className="mt-1 text-2xl font-black text-slate-950">
              {report.score ?? "-"}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase text-slate-500">
              Confianza
            </p>
            <p className="mt-1 text-lg font-black capitalize text-slate-950">
              {report.confidence_level || "media"}
            </p>
          </div>
        </div>

        {generatedAt && (
          <p className="text-xs font-semibold text-slate-500">
            Generado el {generatedAt}
          </p>
        )}

        {[report.demand_summary, report.competition_summary, report.risk_summary]
          .filter(Boolean)
          .map((text) => (
            <p key={text} className="text-sm leading-6 text-slate-700">
              {text}
            </p>
          ))}
      </div>
    </div>
  );
};

const normalizeFeatureText = (text) =>
  (text || "")
    .toString()
    .replace(/Â²/g, "²")
    .replace(/m2/gi, "m²")
    .replace(/\s+/g, " ")
    .trim();

const stripFieldLabel = (value, label) =>
  normalizeFeatureText(value).replace(new RegExp(`^${label}\\s*:\\s*`, "i"), "");

const normalizeQuickBenefits = (benefits) => {
  if (Array.isArray(benefits)) {
    return benefits
      .flatMap((benefit) => {
        const rawBenefit = String(benefit || "").trim();
        if (/^Cerca de:/i.test(rawBenefit)) {
          return [
            rawBenefit
              .split(/\r?\n/)
              .map(normalizeFeatureText)
              .filter(Boolean)
            .join("\n"),
          ];
        }
        return rawBenefit
          .split(/\r?\n/)
          .map((item) => item.trim().replace(/^[-•*\d.)\s]+/, "").trim())
          .map(normalizeFeatureText)
          .filter(Boolean);
      })
      .map((benefit) => (benefit.includes("\n") ? benefit : normalizeFeatureText(benefit)))
      .filter(Boolean);
  }

  const rawBenefits = String(benefits || "").trim();
  if (/^Cerca de:/i.test(rawBenefits)) {
    return [
      rawBenefits
        .split(/\r?\n/)
        .map(normalizeFeatureText)
        .filter(Boolean)
        .join("\n"),
    ];
  }

  return rawBenefits
    .split(/\r?\n/)
    .map((item) => item.trim().replace(/^[-•*\d.)\s]+/, "").trim())
    .map(normalizeFeatureText)
    .filter(Boolean);
};

const getPropertyHighlights = (property) => {
  const details = property.details || {};
  const highlights = [];
  const quickBenefits = normalizeQuickBenefits(details.ventajasRapidas);
  const withMeters = (value) => {
    const cleanValue = normalizeFeatureText(value);
    if (!cleanValue) return "";
    return /m\s*(?:2|²)?$/i.test(cleanValue) ? cleanValue : `${cleanValue} m`;
  };
  const withSquareMeters = (value) => {
    const cleanValue = normalizeFeatureText(value);
    if (!cleanValue) return "";
    return /m\s*(?:2|²)$/i.test(cleanValue)
      ? cleanValue
      : `${cleanValue} m²`;
  };
  const addHighlight = (label, value) => {
    const cleanValue = normalizeFeatureText(value);

    if (!cleanValue || cleanValue === "m²" || cleanValue === "m") return;
    highlights.push({ label, value: cleanValue });
  };

  const measureParts = [
    details.metros
      ? withSquareMeters(details.metros)
      : details.medida || details.medidas || "",
    details.frente ? `Frente ${withMeters(details.frente)}` : "",
    details.fondo ? `Fondo ${withMeters(details.fondo)}` : "",
  ]
    .map(normalizeFeatureText)
    .filter(Boolean);

  addHighlight("Medidas", measureParts.join(" | "));

  addHighlight("Estacionamiento", details.estacionamiento);

  addHighlight("Nivel", details.nivel || details.Nivel);

  const bathrooms =
    details["baños"] || details["baño"] || details.banos || details.bano;

  if (bathrooms) {
    addHighlight("Baños", bathrooms);
  }

  if (quickBenefits.length > 0) {
    return {
      details: highlights.slice(0, 4),
      benefits: quickBenefits,
    };
  }

  return {
    details: highlights.slice(0, 4),
    benefits: [],
  };
};

const RentalDocumentsModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
    <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
      <div className="mb-4 flex items-start justify-between gap-3 border-b pb-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-blue-600">
            Documentacion para renta
          </p>
          <h3 className="text-xl font-bold text-gray-900">
            Documentos base para revisar
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
          aria-label="Cerrar documentacion"
        >
          <X size={18} />
        </button>
      </div>

      <p className="mb-4 text-sm leading-6 text-gray-600">
        {rentalDocuments.intro}
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
          <h4 className="mb-2 font-bold text-gray-900">Persona fisica</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            {rentalDocuments.personaFisica.map((item) => (
              <li key={item} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
          <h4 className="mb-2 font-bold text-gray-900">Persona moral</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            {rentalDocuments.personaMoral.map((item) => (
              <li key={item} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const RentalContractModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
    <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
      <div className="mb-4 flex items-start justify-between gap-3 border-b pb-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-blue-600">
            Contrato de renta
          </p>
          <h3 className="text-xl font-bold text-gray-900">
            Puntos para revisar antes de firmar
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
          aria-label="Cerrar contrato"
        >
          <X size={18} />
        </button>
      </div>

      <p className="mb-4 text-sm leading-6 text-gray-600">
        {rentalContractInfo.intro}
      </p>

      <div className="mb-4 flex flex-wrap gap-2">
        <a
          href={rentalContractInfo.fullContractUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          Ejemplo de contrato
        </a>
      </div>

      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <h4 className="mb-2 font-bold text-gray-900">Revision general</h4>
        <ul className="space-y-2 text-sm text-gray-600">
          {rentalContractInfo.points.map((item) => (
            <li key={item} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 p-4">
        <h4 className="mb-2 font-bold text-amber-900">
          Nota sobre medidor y CFE
        </h4>
        <p className="text-sm leading-6 text-amber-900">
          {rentalContractInfo.cfeNote}
        </p>
      </div>
    </div>
  </div>
);

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationPublishedProperty = location.state?.publishedProperty || null;
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nearby, setNearby] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [businessInput, setBusinessInput] = useState("");
  const [analyzedBusiness, setAnalyzedBusiness] = useState("");
  const [advisorResult, setAdvisorResult] = useState(null);
  const [advisorProperties, setAdvisorProperties] = useState(properties);
  const [hasStartedAgentDemo, setHasStartedAgentDemo] = useState(false);
  const [showRentalContract, setShowRentalContract] = useState(false);
  const [showRentalDocuments, setShowRentalDocuments] = useState(false);
  const [publishedProperty, setPublishedProperty] = useState(navigationPublishedProperty);
  const [proReport, setProReport] = useState(null);
  const [proReportError, setProReportError] = useState("");
  const [isLoadingPublishedProperty, setIsLoadingPublishedProperty] = useState(false);
  const analysisRequestRef = useRef(0);
  const analyzerRef = useRef(null);

  const backTarget = location.state?.fromOwnerPortal
    ? "/mis-propiedades"
    : location.state?.from || "/";

  const property =
    publishedProperty || properties.find((p) => p.id === Number(id));
  const coordinates = getCoordinatesFromProperty(property);
  const advisorEnabled = hasAdvisorAccess(property);
  const isAgentDemoProperty = String(property?.id || "") === demoPropertyId;
  const inegiZoneStats = getPropertyInegiZoneStats(property);
  const giroFromAdvisor = new URLSearchParams(location.search).get("giro") || "";
  const showAgentDemo = new URLSearchParams(location.search).get("demo") === "agentes";
  const showAgentDemoInsights =
    isAgentDemoProperty && showAgentDemo && hasStartedAgentDemo && nearby;
  const zoneAnalysisStorageKey = getZoneAnalysisStorageKey(property?.id);

  useEffect(() => {
    let isMounted = true;
    getPublishedSubmissions()
      .then((published) => {
        if (!isMounted) return;
        const combined = [...properties, ...published];
        setAdvisorProperties(
          combined.filter(
            (item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index
          )
        );
      })
      .catch((error) => console.log("No se cargaron alternativas publicadas:", error));
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!id?.startsWith("supabase-")) {
      setPublishedProperty(null);
      return;
    }

    if (navigationPublishedProperty?.id === id) {
      setPublishedProperty(navigationPublishedProperty);
      setIsLoadingPublishedProperty(false);
      return;
    }

    let isMounted = true;
    setIsLoadingPublishedProperty(true);

    getPublishedSubmissionById(id.replace("supabase-", ""))
      .then((row) => {
        if (isMounted) setPublishedProperty(row);
      })
      .catch((error) => {
        console.log("No se pudo cargar la propiedad publicada:", error);
      })
      .finally(() => {
        if (isMounted) setIsLoadingPublishedProperty(false);
      });

    return () => {
      isMounted = false;
    };
  }, [id, navigationPublishedProperty]);

  useEffect(() => {
    if (!publishedProperty?.submissionId || publishedProperty.plan !== "pro") {
      setProReport(null);
      setProReportError("");
      return;
    }

    let isMounted = true;
    setProReportError("");

    getProReportBySubmissionId(publishedProperty.submissionId)
      .then((report) => {
        if (isMounted) setProReport(report);
      })
      .catch((error) => {
        console.log("No se pudo cargar reporte Pro:", error);
        if (isMounted) setProReportError(error.message);
      });

    return () => {
      isMounted = false;
    };
  }, [publishedProperty?.submissionId, publishedProperty?.plan]);

  useEffect(() => {
    const savedAnalysis = readSavedZoneAnalysis(zoneAnalysisStorageKey);
    if (!savedAnalysis) return;

    if (!giroFromAdvisor.trim() && savedAnalysis.businessInput) {
      setBusinessInput(savedAnalysis.businessInput);
    }

    if (savedAnalysis.nearby) {
      setNearby(savedAnalysis.nearby);
      if (savedAnalysis.businessInput) {
        setAnalyzedBusiness(savedAnalysis.businessInput);
      }
    }
  }, [zoneAnalysisStorageKey, giroFromAdvisor]);

  const handleBusinessInputChange = (value) => {
    setBusinessInput(value);
    saveZoneAnalysis(zoneAnalysisStorageKey, {
      businessInput: value,
    });
  };

  const runZoneAnalysis = useCallback(
    async (businessType = "") => {
      const trimmedBusiness = businessType.trim();
      if (!advisorEnabled || !coordinates || !trimmedBusiness) return;

      const requestId = Date.now();
      analysisRequestRef.current = requestId;

      try {
        setIsAnalyzing(true);
        setAnalysisError("");
        setNearby(null);
        setAnalyzedBusiness("");
        setAdvisorResult(null);
        saveZoneAnalysis(zoneAnalysisStorageKey, {
          businessInput: trimmedBusiness,
          status: "processing",
        });

        const data = await getNearbyPlaces(
          coordinates.lat,
          coordinates.lng,
          trimmedBusiness,
          {
            propertySubmissionId: property.submissionId,
            propertyPublicId: property.id,
          }
        );

        if (analysisRequestRef.current === requestId) {
          const result = await evaluateAdvisorMatch({
            property,
            businessQuery: trimmedBusiness,
            zoneInsight: data,
            allProperties: advisorProperties,
          });
          setNearby(data);
          setAnalyzedBusiness(trimmedBusiness);
          setAdvisorResult(result);
          logAdvisorDecision({
            property,
            businessQuery: trimmedBusiness,
            zoneInsight: data,
            advisorResult: result,
            coordinates,
          });
          saveZoneAnalysis(zoneAnalysisStorageKey, {
            businessInput: trimmedBusiness,
            nearby: data,
            status: "completed",
          });
        }
      } catch (error) {
        if (analysisRequestRef.current === requestId) {
          console.log("ERROR ANALISIS ZONA:", error);
          saveZoneAnalysis(zoneAnalysisStorageKey, {
            businessInput: trimmedBusiness,
            status: "failed",
          });
          setAnalysisError(
            error?.code === "ZONE_CACHE_MISS"
              ? demoSuggestionMessage
              : "No se pudo consultar el entorno en este momento. Intenta de nuevo."
          );
        }
      } finally {
        if (analysisRequestRef.current === requestId) {
          setIsAnalyzing(false);
        }
      }
    },
    [
      coordinates?.lat,
      coordinates?.lng,
      property?.id,
      property?.submissionId,
      advisorProperties,
      advisorEnabled,
      zoneAnalysisStorageKey,
    ]
  );

  useEffect(() => {
    if (!advisorEnabled || !giroFromAdvisor.trim()) return;
    setBusinessInput(giroFromAdvisor);
    runZoneAnalysis(giroFromAdvisor);
  }, [advisorEnabled, giroFromAdvisor, runZoneAnalysis]);

  useEffect(() => {
    if (!showAgentDemo || !isAgentDemoProperty) return;
    setBusinessInput(demoBusinessInput);
    window.setTimeout(() => {
      analyzerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  }, [showAgentDemo, isAgentDemoProperty]);

  useEffect(() => {
    if (!property || !nearby || !analyzedBusiness.trim() || advisorResult) return;
    let isMounted = true;
    evaluateAdvisorMatch({
      property,
      businessQuery: analyzedBusiness,
      zoneInsight: nearby,
      allProperties: advisorProperties,
    })
      .then((result) => {
        if (isMounted) setAdvisorResult(result);
      })
      .catch((error) => console.log("No se restauro el resultado del asesor:", error));
    return () => {
      isMounted = false;
    };
  }, [advisorProperties, advisorResult, analyzedBusiness, nearby, property]);

  if (isLoadingPublishedProperty) {
    return (
      <div className="p-10">
        <h1 className="text-xl font-bold">Cargando propiedad...</h1>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-10">
        <h1 className="text-xl font-bold">Propiedad no encontrada</h1>
        <button
            onClick={() => navigate(backTarget)}
          className="text-blue-600 font-medium"
        >
          Regresar
        </button>
      </div>
    );
  }

  const media = property.images || [];

  const images = media.filter((item) => {
    const url =
      typeof item === "string" ? item : item?.src || item?.url || "";
    return !url.includes(".mp4");
  });

  const videos = media.filter((item) => {
    const url =
      typeof item === "string" ? item : item?.src || item?.url || "";
    return url.includes(".mp4");
  });
  const highlights = getPropertyHighlights(property);
  const rentTaxLabel = getRentTaxLabel(property);
  const isOwnerPortalView = Boolean(location.state?.fromOwnerPortal);
  const isFreeOwnerPublication =
    isOwnerPortalView && (property.plan || "gratis").toLowerCase() === "gratis";
  const localInfo = [
    { label: "Contrato", value: property.contrato },
    { label: "Zona", value: property.zona || property.zone },
    { label: "Servicios", value: property.servicios },
    { label: "Estado", value: property.estado },
    { label: "Disponibilidad", value: property.disponibilidad },
    { label: "Tiempo de gracia", value: property.tiempoGracia },
    {
      label: "Requisitos de renta",
      value: property.requisitosRenta || property["requisitos de renta"],
    },
  ].filter((item) => item.value);
  const showSavedAdvisor = false;
  const advisorData = showSavedAdvisor
    ? mapProReportToAdvisor(proReport) || property.asesorIA
    : null;

  return (
    <>
      <Header />

      <div className="w-full min-h-screen bg-gradient-to-br from-white to-gray-50">
        <div className="fixed bottom-4 right-4 z-40">
          <ContactAdvertiserButton property={property} fixed />
        </div>

        {/* BOTON REGRESAR */}
        <div className="p-4">
          <button
            onClick={() => navigate(backTarget)}
            className="text-blue-600 font-medium"
          >
            Regresar
          </button>
        </div>

        {/* LAYOUT */}
        <div className="w-full px-2 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 items-start">

            {/* IZQUIERDA */}
            <div>
              <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-4">
                  <span className="mt-1 h-10 w-1.5 shrink-0 rounded-full bg-orange-500" />
                  <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                   Datos rápidos e importantes
                  </p>
                  <p className="mt-0.5 text-base font-bold text-slate-950">
                   Resumen del local
                  </p>
                  </div>
                </div>
                <div className="p-4">
                {highlights.details.length > 0 && (
                  <div className="grid grid-cols-4 gap-1 sm:gap-2">
                    {highlights.details.map((item) => (
                      <div
                        key={`${item.label}-${item.value}`}
                        className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-2 py-2 sm:px-3"
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                          {item.label}
                        </p>
                        <p className="break-words text-xs font-semibold leading-tight text-slate-950 sm:text-sm">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {highlights.benefits.length > 0 && (
                <div className={`${highlights.details.length > 0 ? "mt-3" : ""} rounded-xl border border-slate-200 bg-white px-3 py-3`}>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Ventajas
                      </p>
                      <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold text-orange-700">
                        {highlights.benefits.length}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
                      {highlights.benefits.map((benefit) => (
                        <div
                          key={benefit}
                          className="flex min-w-0 items-start gap-2 border-b border-slate-100 pb-2 text-sm leading-5 text-slate-800 last:border-b-0 sm:last:border-b"
                        >
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
                          <span className={`min-w-0 font-medium ${benefit.includes("\n") ? "whitespace-pre-line text-xs leading-4" : ""}`}>
                          {benefit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                )}
                </div>
              </div>

              {/* GRID */}
              <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Galeria del local
                  </p>
                  <p className="text-sm font-semibold text-slate-950">
                    Fotos, video y vista rapida del espacio.
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                  {images.length + videos.length}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">

                {/* IMAGENES */}
                {images.map((item, i) => {
                  const url =
                    typeof item === "string"
                      ? item
                      : item?.src || item?.url || "";

                  return (
                    <div
                      key={i}
                      className="relative h-32 sm:h-36 lg:h-40 overflow-hidden rounded-xl cursor-pointer ring-1 ring-slate-100 transition hover:scale-[1.02] hover:ring-orange-200"
                      onClick={() => {
                        setSelectedMedia({
                          type: "image",
                          list: images.map((img) =>
                            typeof img === "string"
                              ? img
                              : img?.src || img?.url || ""
                          ),
                        });
                        setCurrentIndex(i);
                      }}
                    >
                      <img
                        src={url}
                        className="w-full h-full object-cover block"
                      />
                      {typeof item === "object" && item?.label && (
                        <div className="absolute bottom-2 left-2 right-2">
                          <span className="inline-block rounded-full bg-[#0056D2] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-md">
                            {item.label}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* VIDEOS */}
                {videos.map((item, i) => {
                  const url =
                    typeof item === "string"
                      ? item
                      : item?.src || item?.url || "";
                  const forceMuted =
                    typeof item === "object" && item?.forceMuted;

                  return (
                    <div
                      key={`video-${i}`}
                      className="h-32 sm:h-36 lg:h-40 overflow-hidden rounded-xl cursor-pointer ring-1 ring-slate-100 transition hover:scale-[1.02] hover:ring-orange-200 relative"
                      onClick={() => {
                        setSelectedMedia({
                          type: "video",
                          url,
                          forceMuted,
                        });
                      }}
                    >
                      <video
                        src={url}
                        className="w-full h-full object-cover pointer-events-none"
                        muted
                        defaultMuted
                        playsInline
                        controlsList={forceMuted ? "nodownload noremoteplayback" : undefined}
                        disablePictureInPicture={forceMuted}
                        onLoadedMetadata={(event) => {
                          event.currentTarget.volume = 0;
                          event.currentTarget.muted = true;
                        }}
                        onPlay={(event) => {
                          event.currentTarget.volume = 0;
                          event.currentTarget.muted = true;
                        }}
                        onVolumeChange={(event) => {
                          event.currentTarget.volume = 0;
                          event.currentTarget.muted = true;
                        }}
                      />

                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                        <span className="rounded-full bg-black/50 px-3 py-1 text-sm font-semibold text-white">Ver video</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>

              {/* MAPA */}
              <PropertyMap
                lat={coordinates?.lat}
                lng={coordinates?.lng}
                title={property.title}
              />

              {/* ANALIZADOR DE NEGOCIO */}
              {advisorEnabled ? (
                <>
<div ref={analyzerRef} className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
  <div className="border-b border-slate-100 bg-white p-4 text-slate-950">
  <div className="flex items-center gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
      <Search size={19} />
    </div>
    <div>
      <h2 className="text-lg font-bold">Analizador de zona + Asesor IA</h2>
      <p className="mt-1 text-sm leading-5 text-slate-600">
        <b>Zona:</b> revisa competencia, comercios y demanda cercana. <b>Asesor IA:</b> combina esos datos con el precio y las caracteristicas del espacio para estimar si funciona para tu negocio.
      </p>
    </div>
  </div>
  </div>

  <div className="p-4">
  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-900">
    <b>Nota:</b> {aiDisclaimerText}
  </div>

  {isAgentDemoProperty && (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-black uppercase tracking-wide text-[#0056D2]">
        Demo de agentes
      </p>
      <p className="mt-1 text-sm leading-6 text-slate-600">
        Conoce el potencial de esta ubicacion para una <b>farmacia</b> y mira
        como los agentes ordenan zona, competencia y señales del espacio.
      </p>
      <button
        type="button"
        onClick={() => {
          setHasStartedAgentDemo(true);
          runZoneAnalysis(demoBusinessInput);
        }}
        disabled={isAnalyzing}
        className="mt-3 rounded-lg bg-[#0056D2] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:bg-slate-300"
      >
        {isAnalyzing ? "Analizando..." : "Analizar zona"}
      </button>
      {analysisError && (
        <p className="mt-3 text-sm font-semibold leading-5 text-red-600">
          {analysisError}
        </p>
      )}
    </div>
  )}

  {!isAgentDemoProperty && (
    <>
  <p className="mb-3 text-sm text-gray-600">
    {giroFromAdvisor
      ? "Puedes ajustar el giro para comparar otro negocio."
      : "Escribe el giro que quieres evaluar."}
  </p>

  <div className="flex flex-col sm:flex-row gap-3">
    <input
      type="text"
      placeholder="Ej: farmacia, tienda de ropa, barberia..."
      value={businessInput}
      onChange={(e) => handleBusinessInputChange(e.target.value)}
      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-100"
      disabled={!coordinates || isAnalyzing}
    />

    <button
      onClick={() => runZoneAnalysis(businessInput)}
      disabled={!coordinates || isAnalyzing || !businessInput.trim()}
      className="rounded-xl bg-orange-500 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-orange-600 disabled:bg-gray-300 disabled:shadow-none"
    >
      {isAnalyzing ? "Analizando..." : "Analizar zona + Asesor IA"}
    </button>
  </div>

  {!coordinates && (
    <p className="text-sm text-orange-600 mt-3">
      Esta propiedad necesita coordenadas o un link de mapa con coordenadas para
      analizar el entorno.
    </p>
  )}

  {analysisError === demoSuggestionMessage && (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-black text-slate-950">
        Prueba nuestro demo de agentes
      </p>
      <p className="mt-1 text-sm leading-6 text-slate-600">
        Esta propiedad aun no tiene activos los agentes de inteligencia artificial, pero puedes ver como funcionan con nuestro demo gratis para entender zona, competencia y potencial del espacio.
      </p>
      <button
        type="button"
        onClick={() => navigate(demoAgentPath)}
        className="mt-3 rounded-lg bg-[#0056D2] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
      >
        Ver demo de agentes
      </button>
    </div>
  )}

  {analysisError && analysisError !== demoSuggestionMessage && (
    <p className="text-sm text-red-600 mt-3">{analysisError}</p>
  )}

  {businessInput.trim() && !isAnalyzing && !nearby && !analysisError && (
    <p className="mt-3 text-xs leading-5 text-slate-500">
      Guardamos este giro para esta propiedad. Si navegas y regresas, no tendras que escribirlo otra vez.
    </p>
  )}

    </>
  )}

  {isAnalyzing && (
    <div className="mt-4 rounded-xl border border-orange-100 bg-orange-50 p-4 text-orange-900">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-orange-600">
          <Search size={20} className="animate-spin" />
        </div>
        <div>
          <p className="text-sm font-bold">Analizando la zona...</p>
          <p className="mt-1 text-xs leading-5">
            Estamos preparando el analisis con competencia directa, negocios cercanos y datos de zona.
          </p>
        </div>
      </div>
    </div>
  )}
  </div>
</div>

              <PropertyAdvisorCard
                businessInput={businessInput}
                advisorResult={advisorResult}
                property={property}
              />

              <ZoneAnalysis nearby={nearby} inegiStats={inegiZoneStats} />
                </>
              ) : (
                <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 p-5">
                  <p className="text-xs font-black uppercase tracking-wide text-orange-700">
                    Disponible con Plan Pro
                  </p>
                  <h2 className="mt-1 text-lg font-black text-slate-950">
                    Analisis de zona + Asesor IA
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Esta publicacion gratuita no incluye la lectura comercial avanzada.
                  </p>
                  <div className="mt-4 rounded-xl border border-amber-200 bg-white px-4 py-3 text-xs leading-5 text-amber-900">
                    <b>Nota:</b> {aiDisclaimerText}
                  </div>
                </div>
              )}
            </div>

            {/* DERECHA */}
            <div className="flex flex-col gap-4 lg:self-stretch">
              <ProReportSummary report={proReport} />
              {proReportError && (property.plan || "").toLowerCase() === "pro" && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                  Reporte Pro pendiente o no disponible todavia.
                </div>
              )}

              {isFreeOwnerPublication && (
                <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
                  <div className="flex items-start gap-3 p-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#0056D2]">
                      <Sparkles className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Mejora tu publicación
                      </p>
                      <h2 className="mt-1 text-lg font-black text-slate-950">
                        Hazte Pro
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        Agrega leads perfilados, análisis de zona y datos para el asesor IA.
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          navigate("/plan-pro", {
                            state: { returnTo: "/mis-propiedades" },
                          })
                        }
                        className="mt-4 inline-flex rounded-lg border border-[#0056D2] bg-white px-4 py-3 text-sm font-black text-[#0056D2] transition hover:bg-blue-50"
                      >
                        Comprar Plan Pro
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                 características del local
                </p>
                <h2 className="font-bold text-lg text-slate-950">
                  información clave
                </h2>
                </div>
                <ContactAdvertiserButton property={property} />
                </div>

                <div className="space-y-2 p-5 text-sm text-slate-700">
                  {property.price && (
                    <p>
                      <b>Renta:</b> {formatPrice(property)}
                      {rentTaxLabel && (
                        <span className="ml-1 font-medium text-slate-500">
                          {rentTaxLabel}
                        </span>
                      )}
                    </p>
                  )}

                  {property.address && (
                    <p>
                      <b>Colonia:</b> {stripFieldLabel(property.address, "Colonia")}
                    </p>
                  )}

                  {property.location && (
                    <p>
                      <b>Calle:</b> {stripFieldLabel(property.location, "Calle")}
                    </p>
                  )}

                  {localInfo.length > 0 && (
                    <div className="mt-4 border-t pt-3">
                      <div className="space-y-2">
                        {localInfo.map((item) => (
                          <p key={item.label}>
                            <b>{item.label}:</b> {item.value}
                            {item.label === "Contrato" && (
                              <>
                                {" "}
                                <button
                                  type="button"
                                  onClick={() => setShowRentalContract(true)}
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700 align-middle transition hover:bg-blue-100"
                                  title="Conoce puntos importantes del contrato"
                                >
                                  *
                                </button>
                              </>
                            )}
                            {item.label === "Requisitos de renta" && (
                              <>
                                {" "}
                                <button
                                  type="button"
                                  onClick={() => setShowRentalDocuments(true)}
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700 align-middle transition hover:bg-blue-100"
                                  title="Conoce los documentos para rentar"
                                >
                                  *
                                </button>
                              </>
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {showAgentDemoInsights && <DemoAudienceCards sidebar />}
              {showSavedAdvisor && <AsesorIASection asesor={advisorData} />}
            </div>

          </div>
        </div>

        {/* LIGHTBOX IMAGEN */}
        {selectedMedia?.type === "image" && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 px-12"
            onClick={() => setSelectedMedia(null)}
          >
            <button
              className="absolute left-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20 sm:left-6"
              aria-label="Imagen anterior"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((prev) =>
                  prev === 0
                    ? selectedMedia.list.length - 1
                    : prev - 1
                );
              }}
            >
              <ChevronLeft size={28} />
            </button>

            <img
              src={selectedMedia.list[currentIndex]}
              className="max-w-[90%] max-h-[90%] rounded-lg"
            />

            <button
              className="absolute right-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20 sm:right-6"
              aria-label="Imagen siguiente"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((prev) =>
                  prev === selectedMedia.list.length - 1
                    ? 0
                    : prev + 1
                );
              }}
            >
              <ChevronRight size={28} />
            </button>
          </div>
        )}

        {/* LIGHTBOX VIDEO */}
        {selectedMedia?.type === "video" && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setSelectedMedia(null)}
          >
            <video
              src={selectedMedia.url}
              className="max-w-[90%] max-h-[90%] rounded-lg"
              controls
              muted
              defaultMuted
              playsInline
              autoPlay
              controlsList={selectedMedia.forceMuted ? "nodownload noremoteplayback" : undefined}
              disablePictureInPicture={selectedMedia.forceMuted}
              onLoadedMetadata={(event) => {
                event.currentTarget.volume = 0;
                event.currentTarget.muted = true;
              }}
              onPlay={(event) => {
                event.currentTarget.volume = 0;
                event.currentTarget.muted = true;
              }}
              onVolumeChange={(event) => {
                event.currentTarget.volume = 0;
                event.currentTarget.muted = true;
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {showRentalDocuments && (
          <RentalDocumentsModal
            onClose={() => setShowRentalDocuments(false)}
          />
        )}

        {showRentalContract && (
          <RentalContractModal
            onClose={() => setShowRentalContract(false)}
          />
        )}
      </div>
    </>
  );
};

export default PropertyDetailPage;
